import { memo } from "react";
import {
  BODY_OPTIONS,
  CONDITION_OPTIONS,
  DRIVE_OPTIONS,
  FUEL_OPTIONS,
  SUSPENSION_OPTIONS,
  TRANSMISSION_OPTIONS,
} from "services/api/sales/listings/consts";
import { MarketplaceDetails } from "services/api/sales/listings/types";
import { FleetVehicle } from "services/api/fleet/vehicles/types";
import { ChipToggle } from "ui/modules/sales/pages/listings/components/ListingEditCard/components/MarketplaceStep/primitives";
import { InfoCard, InfoGrid, Section } from "./primitives";

interface Props {
  vehicle?: FleetVehicle | null;
  details?: MarketplaceDetails | null;
}

// Pomoćnik: mapira code na display tekst iz dropdown opcija.
const labelFor = (opts: { value: string; text: string }[], code: string | null | undefined) => {
  if (!code) return null;
  return opts.find((o) => o.value === code)?.text ?? code;
};

// Prikazuje tehničke specifikacije vozila istim redoslijedom i rasporedom kao
// VehicleSpecsSection na unosu. Prioritet vrijednosti: snapshot iz wizarda
// (marketplaceDetails) → originalno vozilo (FleetVehicle), pa oglas zadrži
// vrijednost koju je korisnik unio čak i ako se vozilo kasnije izmijeni.
const ListingVehicleSpecs: React.FC<Props> = ({ vehicle, details }) => {
  const v = vehicle ?? null;
  const type = v?.vehicleType ?? null;
  const model = v?.vehicleModel ?? null;
  const d = details ?? null;

  const typeName = d?.typeName ?? type?.name ?? "-";
  const modelYear = d?.modelYear ?? model?.year ?? null;

  const powerKw = d?.powerKw ?? v?.kw ?? null;
  const powerHp = v?.hp ?? null;
  const powerText = powerKw && powerHp
    ? `${powerKw} kW / ${powerHp} KS`
    : powerKw
      ? `${powerKw} kW`
      : "-";

  const fuelText = labelFor(FUEL_OPTIONS, d?.fuelTypeCode) ?? type?.fuelType?.name ?? "-";
  const driveText = labelFor(DRIVE_OPTIONS, d?.driveTypeCode) ?? type?.drivenWheels?.name ?? "-";
  const transmissionText = labelFor(TRANSMISSION_OPTIONS, d?.transmissionCode) ?? type?.transmission?.name ?? "-";
  const bodyText = labelFor(BODY_OPTIONS, d?.bodyShapeCode) ?? type?.bodyCode ?? "-";
  const conditionText = labelFor(CONDITION_OPTIONS, d?.vehicleConditionCode) ?? "-";
  const suspensionText = labelFor(SUSPENSION_OPTIONS, d?.suspensionCode) ?? "-";

  const displacement = d?.displacementCm3 ?? (type?.cylinderCapacity ? Number(type.cylinderCapacity) : null);
  const doors = d?.doors ?? type?.numberOfDoors ?? null;
  const seats = d?.seats ?? type?.seats ?? null;
  const km = d?.kmTraveled ?? v?.kmTraveled ?? null;
  const colorName = v?.colorName ?? "-";

  return (
    <Section title="Tehničke specifikacije">
      <InfoGrid columns={3}>
        <InfoCard label="Godina proizvodnje" value={d?.manufacturingYear ?? "-"} />
        <InfoCard label="Motor (gorivo)" value={fuelText} />
        <InfoCard label="Snaga motora (kW)" value={powerText} />

        <InfoCard label="Vrsta pogona" value={driveText} />
        <InfoCard label="Vrsta mjenjača" value={transmissionText} />
        <InfoCard label="Oblik karoserije" value={bodyText} />

        <InfoCard label="Radni obujam (cm³)" value={displacement ? `${displacement} cm³` : "-"} />
        <InfoCard label="Godina modela" value={modelYear ?? "-"} />
        <InfoCard label="Broj vrata" value={doors ?? "-"} />

        <InfoCard label="Broj sjedala" value={seats ?? "-"} />
        <InfoCard label="Ovjes" value={suspensionText} />
        <InfoCard label="Stanje vozila" value={conditionText} />

        <InfoCard label="Boja" value={colorName} />
        <InfoCard label="Prijeđeni kilometri" value={km != null ? `${km.toLocaleString("hr-HR")} km` : "-"} />
        <InfoCard label="Tip" value={typeName} />
      </InfoGrid>

      <div className="mt-4">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Gume (širina / visina / promjer)
        </div>

        <InfoGrid columns={3}>
          <InfoCard label="Širina" value={d?.tireWidth ?? "-"} />
          <InfoCard label="Visina" value={d?.tireHeight ?? "-"} />
          <InfoCard label="Promjer" value={d?.tireDiameter ?? "-"} />
        </InfoGrid>
      </div>

      <div className="mt-4 mb-3">
        <ChipToggle label="Metalik boja" selected={!!d?.metallic} readOnly />
      </div>
    </Section>
  );
};

export default memo(ListingVehicleSpecs);
